import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  DashboardMetricColumns,
  DashboardMetricContentTransformations,
  DEAL_SORTING_VALUES,
  CUSTOM_COL_SIZES,
  HeadingTranslations,
  LOCKED_COLUMN,
} from './data';
import DownloadIcon from 'icons/Download.icon';
import TableControlButton from 'components/TableControlButton';
import utils, {
  checkIsPublished,
  downloadUrlInNewWindow,
  getHasActiveFilters,
  getIsArrayEmpty,
} from 'utils';
import { DashboardMetric } from './types';
import { useNavigate } from 'react-router-dom';
import useCallbackRef from 'hooks/useCallbackRef';
import PaymentInvoiceSkipped from './components/PaymentInvoiceSkipped';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import MultiFilterPanel from 'components/MultiFilterPanel';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import useTableFetchState from 'hooks/useTableFetchState';
import MultiSortPanel from 'components/MultiSortPanel';
import useListingMetricsFilters from './hooks/useListingMetricsFilters';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import ManageColumns from 'components/ManageColumns';

import './ListingMetrics.styles.scss';

const RELATIONS = '["listing","dealRoom","user"]';

type ListingMetricsProps = {
  className?: string;
};

const ListingMetrics: React.FC<ListingMetricsProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();

  const { popServerError } = useContext(PopNotificationsContext);

  const [isDownloading, setIsDownloading] = useState(false);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: dealMetrics,
    setIsLoading,
    setData,
    setPaginatedData,
  } = useTableFetchState<DashboardMetric>();

  const [dashboardMetric, setDashboardMetric] = useState<DashboardMetric>();

  const [bmResponse, bmResponseRef] = useCallbackRef<ModalRef>();

  const { params } = useQueryParams();

  const {
    sort,
    page,
    timeframe,
    hideMetrics,
    brokerName,
    listingName,
    stage,
    paidPackage,
    paymentStatus,
    hasIM,
    hasNDA,
    lastEditedTimeframe,
    brokerageFirm,
  } = params;

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const classes = classNames('listing-metrics', className);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  } = useListingMetricsFilters();

  const isEmpty = !isLoading && getIsArrayEmpty(dealMetrics);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const navigate = useNavigate();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.LISTING_METRICS,
    DashboardMetricColumns,
  );

  const hideMetricsFilter = useMemo(
    () =>
      hideMetrics
        ? utils.mapParamsToArray(hideMetrics).reduce(
            (acc, filterKey) => ({
              ...acc,
              [filterKey]: false,
            }),
            {},
          )
        : {},
    [hideMetrics],
  );

  const handleOnRowClick = useCallback(
    ({ listing }: DashboardMetric) => {
      if (!listing) return;

      const { industry, slugName, type, stage } = listing;

      const isPublished = checkIsPublished(stage);

      if (!isPublished || !industry || !slugName || !type) return;

      const redirectionListingLink = utils.composeBusinessUrl(
        industry,
        slugName,
        type,
      );

      navigate(redirectionListingLink);
    },
    [navigate],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.portfolio.admin.getDashboardMetrics({
          $sort: sort,
          $page: page,
          $limit: limit,
          $relations: RELATIONS,
          brokerName,
          listingName,
          timeframe,
          stage,
          paidPackage,
          paymentStatus,
          hasIM,
          hasNDA,
          lastEditedTimeframe,
          brokerageFirm,
          ...hideMetricsFilter,
        });
        setPaginatedData(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    page,
    limit,
    brokerName,
    listingName,
    sort,
    timeframe,
    stage,
    paidPackage,
    paymentStatus,
    hasIM,
    hasNDA,
    lastEditedTimeframe,
    brokerageFirm,
    hideMetricsFilter,
    popServerError,
    setPaginatedData,
    setIsLoading,
  ]);

  const updateDashboardMetric = useCallback(
    (dashboardMetric: Partial<DashboardMetric>) =>
      setData((old) =>
        old.map((el) =>
          el._id === dashboardMetric._id ? { ...el, ...dashboardMetric } : el,
        ),
      ),
    [setData],
  );

  const openInvoiceModal = useCallback(
    (metric: DashboardMetric) => {
      setDashboardMetric(metric);
      bmResponse?.open();
    },
    [bmResponse],
  );

  const renderedContent = useMemo(
    () =>
      DashboardMetricColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (dashboardMetric: DashboardMetric) =>
            DashboardMetricContentTransformations[col](
              dashboardMetric,
              openInvoiceModal,
            ),
        }),
        {},
      ),
    [openInvoiceModal],
  );

  const downloadCSV = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url, type },
      } = await api.portfolio.admin.getDashboardMetricsCsv();

      if (url) downloadUrlInNewWindow(url, type);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [popServerError]);

  return (
    <div className={classes}>
      <div className="admin-dashboard-router__heading">
        <h1>{t('listingMetrics')}</h1>

        <div className="listing-metrics__searching-cont">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />

              <MultiSortPanel
                ref={multiSortRef}
                options={translatedSortOptions}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}

          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HeadingTranslations}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />

          <TableControlButton
            className="export-button"
            icon={DownloadIcon}
            tooltip={t('logCsv')}
            disabled={isDownloading}
            onClick={downloadCSV}
          />
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <ReorderResizeTable
        className={classes}
        columns={columns}
        data={dealMetrics}
        totalPages={totalPages}
        totalItems={totalItems}
        isDataLoading={isLoading}
        sortingValues={DEAL_SORTING_VALUES}
        columnFilterOptions={columnFilterOptions}
        customSizes={CUSTOM_COL_SIZES}
        headingTranslations={HeadingTranslations}
        tableContentTransformations={renderedContent}
        onRowClick={handleOnRowClick}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder}
      />

      <PaymentInvoiceSkipped
        ref={bmResponseRef}
        dashboardMetric={dashboardMetric}
        updateDashboardMetric={updateDashboardMetric}
      />
    </div>
  );
};

export default ListingMetrics;
